import React, {Fragment, useState, useEffect, useRef} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {snakeCase, camelCase} from 'change-case';
import {useTranslate} from '@computerrock/formation-i18n';
import {resolveRoute} from '@computerrock/formation-router';
import {apmContractPartnerRegionTypes, apmContractPartnerContractStatusTypes, apmTemporaryRestrictionCategoryTypes, apmACEPartnerServiceTypes, ampVehicleWeightTypes, apmVehicleStorageServiceTypes, apmContractPartnerAdditionalServiceTypes, apmContractPartnerExternalCommissionerTypes, apmContractPartnerAdditionalFeatureTypes, apmContractPartnerAssignmentChannelTypes, europeanCountries, apmContractPartnerServiceTonnageTypes, apmContractPartnerVehicleCompatibilityTypes, apmContractPartnerEquipmentTypes, apmContractPartnerUndergroundParkingTypes, apmRentalCarServiceOptionTypes, persistenceStates} from '@ace-de/eua-entity-types';
import {Badge, NoResultsBlock, Paginator, Panel, useStyles, Option, ButtonIcon, RangeInput, HighlightCircle, LoaderSpinner} from '@ace-de/ui-components';
import {InputField, SelectField, Checkbox} from '@ace-de/ui-components/form';
import {Icon, waitingIcon, InteractiveIcon, findCaseIcon, searchIcon, resetIcon, questionmarkIcon, noServiceIcon, otherIcon, arrowDownIcon, arrowUpIcon, plusIcon} from '@ace-de/ui-components/icons';
import {Table, TableBody, TableCaption, TableCell, TableHead, TableRow} from '@ace-de/ui-components/data-elements';
import routePaths from '../routePaths';
import * as contractPartnerSelectors from './contractPartnerSelectors';
import config from '../config';
import * as contractPartnerActionTypes from './contractPartnerActionTypes';
import vehicleTransportsDistances from './vehicleTransportDistances';
import germanStateTypes from './germanStateTypes';
import germanStateCodes from './germanStateCodes';
import ecsAccessControl from '../ecsAccessControl';
import {ecsFeatures, ecsFeatureActions} from '../application/ecsFeatures';

const multiselectFields = [
    'regions',
    'contractStatus',
    'states',
    'serviceTypes',
    'compatibilities',
    'serviceAreaCountries',
    'transportationOptions',
    'vehicleStorage',
    'additionalServices',
    'additionalFeatures',
    'externalCommissioners',
    'assignmentChannels',
    'relation',
    'equipment',
    'undergroundParking',
    'rentalCarOptions',
    'carDismantling',
];

const initialContractPartnerFilteringParams = {
    searchTerm: '',
    regions: '',
    contractStatus: '',
    fixedAreaOfApplication: '',
    postCodeFrom: '',
    postCodeTo: '',
    states: '',
    serviceTypes: '',
    tonnageThreshold: '',
    compatibilities: '',
    serviceAreaCountries: '',
    transportationOptions: '',
    distanceThreshold: '',
    vehicleStorage: '',
    additionalServices: '',
    additionalFeatures: '',
    externalCommissioners: '',
    relation: '',
    ellaActive: '',
    assignmentChannels: '',
    fixedPrice: [config.MIN_CONTRACT_PARTNER_FIXED_PRICE, config.MAX_CONTRACT_PARTNER_FIXED_PRICE],
    isFixedPriceEnabled: false,
    equipment: '',
    undergroundParking: '',
    rentalCarOptions: '',
    hasWaitingArea: '',
    hasCamper: '',
    carDismantling: '',
};

const basicServiceTypes = [
    apmACEPartnerServiceTypes.TOWING,
    apmACEPartnerServiceTypes.RECOVERY,
    apmACEPartnerServiceTypes.ROADSIDE_ASSISTANCE,
];

const availableAssignmentChannels = [
    apmContractPartnerAssignmentChannelTypes.DRIVER_APP,
    apmContractPartnerAssignmentChannelTypes.EMAIL_PDF_XML,
    apmContractPartnerAssignmentChannelTypes.FAX,
    apmContractPartnerAssignmentChannelTypes.MANUAL_PHONE,
];

const carDisposalOptionTypes = {
    CAR_DISMANTLING: 'CAR_DISMANTLING',
    USED_CAR_STATION: 'USED_CAR_STATION',
};

const transportationOptionTypes = {
    WITH_PASSENGER: 'WITH_PASSENGER',
    WITHOUT_PASSENGER: 'WITHOUT_PASSENGER',
};

const relationOptionTypes = {
    PARENT: 'PARENT',
    CHILD: 'CHILD',
    HAS_OPERATING_UNIT: 'HAS_OPERATING_UNIT',
};

const sortingOptions = {
    ASC: 'ASC',
    DESC: 'DESC',
};

const categoryIconMap = {
    [apmTemporaryRestrictionCategoryTypes.RESTRICTED_SERVICES]: questionmarkIcon,
    [apmTemporaryRestrictionCategoryTypes.TEMPORARILY_CLOSED]: noServiceIcon,
    [apmTemporaryRestrictionCategoryTypes.WAITING_TIME]: waitingIcon,
    [apmTemporaryRestrictionCategoryTypes.OTHER]: otherIcon,
};

const ContractPartnerSearchScreen = props => {
    const {cx} = useStyles();
    const {createTranslateShorthand, translate} = useTranslate();
    const translateScreen = createTranslateShorthand('contract_partner_search_screen');
    const {history, contractPartnerSearchResults, contractPartnerSearchResultsCount} = props;
    const {initiateCreateTemporaryRestrictionFlow, searchPersistenceState} = props;
    const [queryParams, setQueryParams] = useState(new URLSearchParams());
    const paginatorCount = Math.ceil(contractPartnerSearchResultsCount / config.DEFAULT_PAGE_SIZE);
    const [areFiltersExpanded, setAreFiltersExpanded] = useState(false);
    const [formData, setFormData] = useState(initialContractPartnerFilteringParams);
    const [sortingDirection, setSortingDirection] = useState(queryParams.get('sort')
        ? {sort: queryParams.get('sort')}
        : {sort: `address.postCode,${sortingOptions.ASC}`});
    const topElementRef = useRef(null);
    const isTemporaryRestrictionAvailable = ecsAccessControl.grantFeatureAccess(
        ecsFeatures.TEMPORARY_RESTRICTION,
        ecsFeatureActions.CREATE,
    );
    const isSearchInProgress = searchPersistenceState === persistenceStates.PENDING;

    const openContractPartnerScreen = contractPartner => {
        history.push(resolveRoute(routePaths.CONTRACT_PARTNER, {
            contractPartnerId: contractPartner.id,
        }));
    };

    useEffect(() => {
        if (typeof history.location.search === 'string') {
            const newQueryParams = new URLSearchParams(history.location.search);
            if (newQueryParams.toString() !== queryParams.toString()) {
                setQueryParams(newQueryParams);
                // handle new query parameters and update form data
                const multiselectFieldsValues = {};
                multiselectFields.forEach(fieldName => {
                    multiselectFieldsValues[fieldName] = newQueryParams.getAll(fieldName).length
                        ? newQueryParams.getAll(fieldName) : '';
                });

                const fixedPriceFrom = newQueryParams.get('fixedPriceFrom') || config.MIN_CONTRACT_PARTNER_FIXED_PRICE;
                const fixedPriceTo = newQueryParams.get('fixedPriceTo') || config.MAX_CONTRACT_PARTNER_FIXED_PRICE;

                setFormData({
                    searchTerm: newQueryParams.get('searchTerm') || '',
                    fixedAreaOfApplication: newQueryParams.get('fixedAreaOfApplication')
                        ? (newQueryParams.get('fixedAreaOfApplication') === 'true') : '',
                    postCodeFrom: newQueryParams.get('postCodeFrom') || '',
                    postCodeTo: newQueryParams.get('postCodeTo') || '',
                    ...multiselectFieldsValues,
                    ellaActive: newQueryParams.get('ellaActive')
                        ? (newQueryParams.get('ellaActive') === 'true') : '',
                    hasWaitingArea: newQueryParams.get('hasWaitingArea')
                        ? (newQueryParams.get('hasWaitingArea') === 'true') : '',
                    hasCamper: newQueryParams.get('hasCamper')
                        ? (newQueryParams.get('hasCamper') === 'true') : '',
                    fixedPrice: [fixedPriceFrom, fixedPriceTo],
                    isFixedPriceEnabled: newQueryParams.get('isFixedPriceEnabled')
                        ? (newQueryParams.get('isFixedPriceEnabled') === 'true') : false,
                    tonnageThreshold: newQueryParams.get('tonnageThreshold')
                        ? parseFloat(newQueryParams.get('tonnageThreshold')) : '',
                    distanceThreshold: newQueryParams.get('distanceThreshold')
                        ? parseFloat(newQueryParams.get('distanceThreshold')) : '',
                });
            }
        }
    }, [history.location.search, queryParams]);

    const handlePaginationPage = page => {
        topElementRef.current?.scrollIntoView({behavior: 'smooth'});
        const apiQueryParams = new URLSearchParams(queryParams);
        apiQueryParams.set('page', `${page}`);
        apiQueryParams.set('size', `${config.DEFAULT_PAGE_SIZE}`);

        const queryParamsString = apiQueryParams ? apiQueryParams.toString() : '';
        history.push(resolveRoute(routePaths.CONTRACT_PARTNER_SEARCH, {}, {search: queryParamsString}));
    };

    const formatQueryParams = formData => {
        if (!formData) return;
        const apiQueryParams = new URLSearchParams();

        Object.keys(formData).forEach(formField => {
            // handle fixedPrice parameters
            if (formField === 'fixedPrice' && Array.isArray(formData[formField])) {
                const fixedPrice = formData[formField];
                if (formData['isFixedPriceEnabled']) {
                    apiQueryParams.append('fixedPriceFrom', fixedPrice[0]);
                    apiQueryParams.append('fixedPriceTo', fixedPrice[1]);
                }
                return;
            }

            // handle multiselect fields
            if (multiselectFields.includes(formField) && Array.isArray(formData[formField])) {
                formData[formField].forEach(fieldValue => {
                    if (fieldValue) {
                        apiQueryParams.append(`${formField}`, fieldValue);
                    }
                });
                return;
            }

            if (formData[formField] !== undefined && formData[formField] !== '') {
                apiQueryParams.append(`${formField}`, formData[formField]);
            }
        });
        return apiQueryParams;
    };

    const onSubmit = () => {
        if (!formData) return;

        const apiQueryParams = formatQueryParams({
            ...formData,
            ...sortingDirection,
        });

        const queryParamsString = apiQueryParams ? apiQueryParams.toString() : '';

        history.push(resolveRoute(routePaths.CONTRACT_PARTNER_SEARCH, {}, {search: queryParamsString}));
    };

    const onChange = (key, value) => {
        setFormData(prevState => ({
            ...prevState,
            [key]: value,
        }));
    };

    const handleResetFilter = () => {
        setFormData(initialContractPartnerFilteringParams);

        const queryParams = new URLSearchParams(history.location.search);
        const apiQueryParams = formatQueryParams(sortingDirection);
        const queryParamsString = queryParams.get('sort') ? apiQueryParams.toString() : '';

        history.push(resolveRoute(routePaths.CONTRACT_PARTNER_SEARCH, {}, {search: queryParamsString}));
    };

    const handleCreateRestriction = (event, contractPartner) => {
        event.stopPropagation();

        initiateCreateTemporaryRestrictionFlow({contractPartner});
    };

    return (
        <Fragment>
            <Panel
                qaIdent="contract-partner-search-filter"
                title={translateScreen('panel_title.filter_and_search')}
                contentClassName={cx('global!ace-u-flex', 'global!ace-u-flex--direction-column')}
            >
                <Fragment>
                    <div className={cx('global!ace-u-grid', 'global!ace-u-margin--bottom-32')}>
                        <div
                            className={cx('global!ace-u-grid-column--span-7')}
                            onKeyDown={event => {
                                if (event.key === 'Enter') onSubmit();
                            }}
                        >
                            <InputField
                                name="searchTerm"
                                value={formData?.searchTerm}
                                label={translateScreen('input_field_label.free_search')}
                                className={cx(['global!ace-u-width--full', 'global!ace-u-margin--top-8'])}
                                onChange={value => onChange('searchTerm', value)}
                            />
                        </div>
                        <div className={cx('global!ace-u-grid-column--span-2')}>
                            <SelectField
                                name="contractStatus"
                                value={formData?.contractStatus}
                                label={translateScreen('select_field_label.status')}
                                className={cx(['global!ace-u-width--full', 'global!ace-u-margin--top-8'])}
                                onChange={value => onChange('contractStatus', value)}
                                isMultipleChoice={true}
                            >
                                {Object.keys(apmContractPartnerContractStatusTypes).map(statusType => (
                                    <Option
                                        key={statusType}
                                        value={statusType}
                                        name={`contractStatusOption${camelCase(statusType)}`}
                                    >
                                        {translate(`global.contract_status.${snakeCase(statusType)}`)}
                                    </Option>
                                ))}
                            </SelectField>
                        </div>
                        <div className={cx('global!ace-u-grid-column--span-2')}>
                            <SelectField
                                name="sort"
                                label={translateScreen('select_field_label.sort')}
                                className={cx(['global!ace-u-width--full', 'global!ace-u-margin--top-8'])}
                                value={sortingDirection.sort}
                                onChange={value => setSortingDirection({sort: value})}
                            >
                                {Object.keys(sortingOptions)
                                    .map(sortingOption => (
                                        <Option
                                            key={sortingOption}
                                            value={`address.postCode,${sortingOption}`}
                                            name={`sortingOption${camelCase(sortingOption)}`}
                                        >
                                            {translateScreen(`select_field_option.${sortingOption.toLowerCase()}`)}
                                        </Option>
                                    ))}
                            </SelectField>
                        </div>
                        <div
                            className={cx([
                                'global!ace-u-grid-column--span-1',
                                'global!ace-u-flex',
                                'global!ace-u-flex--justify-flex-end',
                                'global!ace-u-padding--top-24',
                            ])}
                        >
                            <ButtonIcon
                                name="searchButton"
                                icon={searchIcon}
                                onClick={onSubmit}
                                className={cx('global!ace-u-margin--top-8')}
                            />
                        </div>
                    </div>
                    {areFiltersExpanded && (
                        <Fragment>
                            <div className={cx(['global!ace-u-typography--variant-body-medium', 'global!ace-u-margin--bottom-8'])}>
                                {translateScreen('text.region')}
                            </div>
                            <div
                                className={cx([
                                    'global!ace-u-flex',
                                    'global!ace-u-flex--column-gap-24',
                                    'global!ace-u-margin--bottom-32',
                                ])}
                            >
                                <SelectField
                                    name="regions"
                                    value={formData?.regions}
                                    label={translateScreen('select_field_label.region')}
                                    className={cx('global!ace-u-width--256')}
                                    onChange={value => onChange('regions', value)}
                                    isMultipleChoice={true}
                                >
                                    {Object.keys(apmContractPartnerRegionTypes).map(region => (
                                        <Option
                                            key={region}
                                            value={region}
                                            name={`regionOption${camelCase(region)}`}
                                        >
                                            {translate(`global.region.${snakeCase(region)}`)}
                                        </Option>
                                    ))}
                                </SelectField>
                                <InputField
                                    name="postCodeFrom"
                                    value={formData?.postCodeFrom}
                                    label={translateScreen('input_field_label.post_code_area_from')}
                                    className={cx('global!ace-u-width--128')}
                                    placeholder={translateScreen('input_field_placeholder.from', {postCodeFrom: config.POST_CODE_FROM})}
                                    onChange={value => onChange('postCodeFrom', value)}
                                />
                                <InputField
                                    name="postCodeTo"
                                    value={formData?.postCodeTo}
                                    label={translateScreen('input_field_label.post_code_area_to')}
                                    className={cx('global!ace-u-width--128')}
                                    placeholder={translateScreen('input_field_placeholder.to', {postCodeTo: config.POST_CODE_TO})}
                                    onChange={value => onChange('postCodeTo', value)}
                                />
                                <SelectField
                                    name="states"
                                    value={formData?.states}
                                    label={translateScreen('select_field_label.state')}
                                    className={cx('global!ace-u-width--256')}
                                    onChange={value => onChange('states', value)}
                                    isMultipleChoice={true}
                                >
                                    {Object.keys(germanStateTypes).map(stateKey => (
                                        <Option
                                            key={stateKey}
                                            value={germanStateCodes[stateKey]} // the value is a state code!
                                            name={`stateOption${camelCase(stateKey)}`}
                                        >
                                            {translate(`global.german_state.${snakeCase(stateKey)}`)}
                                        </Option>
                                    ))}
                                </SelectField>
                            </div>

                            <div className={cx(['global!ace-u-typography--variant-body-medium', 'global!ace-u-margin--bottom-8'])}>
                                {translateScreen('text.basic_services')}
                            </div>
                            <div
                                className={cx([
                                    'global!ace-u-flex',
                                    'global!ace-u-flex--column-gap-24',
                                    'global!ace-u-margin--bottom-32',
                                ])}
                            >
                                <SelectField
                                    name="serviceTypes"
                                    value={formData?.serviceTypes}
                                    label={translateScreen('select_field_label.service')}
                                    className={cx('global!ace-u-width--256')}
                                    onChange={value => onChange('serviceTypes', value)}
                                    isMultipleChoice={true}
                                >
                                    {basicServiceTypes.map(service => (
                                        <Option
                                            key={service}
                                            value={service}
                                            name={`serviceOption${camelCase(service)}`}
                                        >
                                            {translate(`global.basic_service_type.${snakeCase(service)}`)}
                                        </Option>
                                    ))}
                                </SelectField>
                                <SelectField
                                    name="tonnageThreshold"
                                    value={formData?.tonnageThreshold}
                                    label={translateScreen('select_field_label.tonnage')}
                                    className={cx('global!ace-u-width--128')}
                                    onChange={value => onChange('tonnageThreshold', value)}
                                >
                                    <Option
                                        name="tonnageOptionNone"
                                        value=""
                                    >
                                        {translateScreen('select_field_option_placeholder.select_option')}
                                    </Option>
                                    {Object.keys(apmContractPartnerServiceTonnageTypes)
                                        .filter(tonnageType => {
                                            return tonnageType !== apmContractPartnerServiceTonnageTypes.NO_SERVICES;
                                        })
                                        .map(tonnageType => (
                                            <Option
                                                key={tonnageType}
                                                value={tonnageType === apmContractPartnerServiceTonnageTypes
                                                    .ULTRA_HEAVY_WEIGHT_VEHICLE
                                                    ? config.MIN_ULTRA_HEAVY_WEIGHT_VEHICLE_TONNAGE
                                                    : ampVehicleWeightTypes[tonnageType]}
                                                name={`tonnageOption${camelCase(tonnageType)}`}
                                            >
                                                {translate(`global.vehicle_tonnage_type.${snakeCase(tonnageType)}`)}
                                            </Option>
                                        ))}
                                </SelectField>
                                <SelectField
                                    name="compatibilities"
                                    value={formData?.compatibilities}
                                    label={translateScreen('select_field_label.vehicle_type')}
                                    className={cx('global!ace-u-width--256')}
                                    onChange={value => onChange('compatibilities', value)}
                                    isMultipleChoice={true}
                                >
                                    {Object.keys(apmContractPartnerVehicleCompatibilityTypes).map(vehicleType => (
                                        <Option
                                            key={vehicleType}
                                            value={vehicleType}
                                            name={`vehicleTypeOption${camelCase(vehicleType)}`}
                                        >
                                            {translate(`global.vehicle_compatibility_type.${snakeCase(vehicleType)}`)}
                                        </Option>
                                    ))}
                                </SelectField>
                                <SelectField
                                    name="serviceAreaCountries"
                                    value={formData?.serviceAreaCountries}
                                    label={translateScreen('select_field_label.foreign_countries')}
                                    className={cx('global!ace-u-width--256')}
                                    onChange={value => onChange('serviceAreaCountries', value)}
                                    isMultipleChoice={true}
                                >
                                    {config.GERMAN_NEIGHBORING_COUNTRIES.map(countryCode => (
                                        <Option
                                            key={countryCode}
                                            value={snakeCase(europeanCountries[countryCode].name).toUpperCase()}
                                            name={`foreignCountryOption${camelCase(countryCode)}`}
                                        >
                                            {translate(
                                                `global.country.${snakeCase(europeanCountries[countryCode].name)}`,
                                            )}
                                        </Option>
                                    ))}
                                </SelectField>
                            </div>
                            <div className={cx(['global!ace-u-typography--variant-body-medium', 'global!ace-u-margin--bottom-8'])}>
                                {translateScreen('text.additional_services')}
                            </div>
                            <div
                                className={cx([
                                    'global!ace-u-flex',
                                    'global!ace-u-flex--column-gap-24',
                                    'global!ace-u-margin--bottom-32',
                                ])}
                            >
                                <SelectField
                                    name="transportationOptions"
                                    value={formData?.transportationOptions}
                                    label={translateScreen('select_field_label.transport')}
                                    className={cx('global!ace-u-width--256')}
                                    onChange={value => onChange('transportationOptions', value)}
                                    isMultipleChoice={true}
                                >
                                    {Object.keys(transportationOptionTypes).map(transporationOption => (
                                        <Option
                                            key={transporationOption}
                                            value={transporationOption}
                                            name={`transportationOption${camelCase(transporationOption)}`}
                                        >
                                            {translateScreen(`select_field_option.${snakeCase(transporationOption)}`)}
                                        </Option>
                                    ))}
                                </SelectField>
                                <SelectField
                                    name="distanceThreshold"
                                    value={formData?.distanceThreshold}
                                    label={translateScreen('select_field_label.distance')}
                                    className={cx('global!ace-u-width--128')}
                                    onChange={value => onChange('distanceThreshold', value)}
                                >
                                    <Option
                                        name="transportDistanceNone"
                                        value=""
                                    >
                                        {translateScreen('select_field_option_placeholder.select_option')}
                                    </Option>
                                    {vehicleTransportsDistances
                                    // do not include 0 as an option
                                        .filter(distance => !!distance)
                                        .map(distance => (
                                            <Option
                                                key={`transportDistance${distance}`}
                                                value={distance}
                                                name={`transportDistance${distance}`}
                                            >
                                                {translate(`global.vehicle_transport_distance_type.${distance}`)}
                                            </Option>
                                        ))}
                                </SelectField>
                                <SelectField
                                    name="vehicleStorages"
                                    value={formData?.vehicleStorage}
                                    label={translateScreen('select_field_label.storage')}
                                    className={cx('global!ace-u-width--256')}
                                    onChange={value => onChange('vehicleStorage', value)}
                                    isMultipleChoice={true}
                                >
                                    {Object.keys(apmVehicleStorageServiceTypes).map(storage => (
                                        <Option
                                            key={storage}
                                            value={storage}
                                            name={`storageOption${camelCase(storage)}`}
                                        >
                                            {translate(`global.vehicle_storage_service_type.${snakeCase(storage)}`)}
                                        </Option>
                                    ))}
                                </SelectField>
                                <SelectField
                                    name="additionalServices"
                                    value={formData?.additionalServices}
                                    label={translateScreen('select_field_label.additional_services')}
                                    className={cx('global!ace-u-width--256')}
                                    onChange={value => onChange('additionalServices', value)}
                                >
                                    {Object.keys(apmContractPartnerAdditionalServiceTypes)
                                        .map(additionalServices => (
                                            <Option
                                                key={additionalServices}
                                                value={additionalServices}
                                                name={`additionalServiceOption${camelCase(additionalServices)}`}
                                            >
                                                {translate(
                                                    `global.contract_partner_additional_services.${snakeCase(
                                                        additionalServices,
                                                    )}`,
                                                )}
                                            </Option>
                                        ))}
                                </SelectField>
                            </div>
                            <div className={cx(['global!ace-u-typography--variant-body-medium', 'global!ace-u-margin--bottom-8'])}>
                                {translateScreen('text.operating_features')}
                            </div>
                            <div
                                className={cx([
                                    'global!ace-u-flex',
                                    'global!ace-u-flex--column-gap-24',
                                    'global!ace-u-margin--bottom-32',
                                ])}
                            >
                                <SelectField
                                    name="equipment"
                                    value={formData?.equipment}
                                    label={translateScreen('select_field_label.equipment')}
                                    className={cx('global!ace-u-width--256')}
                                    onChange={value => onChange('equipment', value)}
                                    isMultipleChoice={true}
                                >
                                    {Object.keys(apmContractPartnerEquipmentTypes).map(equipmentType => (
                                        <Option
                                            key={equipmentType}
                                            value={equipmentType}
                                            name={`equipmentOption${camelCase(equipmentType)}`}
                                        >
                                            {translate(
                                                `global.contract_partner_equipment_type.${snakeCase(equipmentType)}`,
                                            )}
                                        </Option>
                                    ))}
                                </SelectField>
                                <SelectField
                                    name="undergroundParking"
                                    value={formData?.undergroundParking}
                                    label={translateScreen('select_field_label.underground_parking')}
                                    className={cx('global!ace-u-width--256')}
                                    onChange={value => onChange('undergroundParking', value)}
                                    isMultipleChoice={true}
                                >
                                    {Object.keys(apmContractPartnerUndergroundParkingTypes).map(parkingType => (
                                        <Option
                                            key={parkingType}
                                            value={parkingType}
                                            name={`parkingOption${camelCase(parkingType)}`}
                                        >
                                            {translate(
                                                `global.contract_partner_underground_parking_type.${snakeCase(
                                                    parkingType,
                                                )}`,
                                            )}
                                        </Option>
                                    ))}
                                </SelectField>
                                <SelectField
                                    name="rentalCarOptions"
                                    value={formData?.rentalCarOptions}
                                    label={translateScreen('select_field_label.rental_vehicles')}
                                    className={cx('global!ace-u-width--256')}
                                    onChange={value => onChange('rentalCarOptions', value)}
                                    isMultipleChoice={true}
                                >
                                    {Object.keys(apmRentalCarServiceOptionTypes).map(rentalCarOption => (
                                        <Option
                                            key={rentalCarOption}
                                            value={rentalCarOption}
                                            name={`rentalCarOption${camelCase(rentalCarOption)}`}
                                        >
                                            {translate(
                                                `global.contract_partner_rental_car_service_option.${snakeCase(
                                                    rentalCarOption,
                                                )}`,
                                            )}
                                        </Option>
                                    ))}
                                </SelectField>
                                <SelectField
                                    name="hasWaitingArea"
                                    value={formData?.hasWaitingArea}
                                    label={translateScreen('select_field_label.has_waiting_area')}
                                    className={cx('global!ace-u-width--128')}
                                    onChange={value => onChange('hasWaitingArea', value)}
                                >
                                    <Option
                                        name="hasWaitingAreaOptionNone"
                                        value=""
                                    >
                                        {translateScreen('select_field_option_placeholder.select_option')}
                                    </Option>
                                    <Option
                                        name="hasWaitingAreaOptionTrue"
                                        value={true}
                                    >
                                        {translateScreen('select_field_option.yes')}
                                    </Option>
                                    <Option
                                        name="hasWaitingAreaOptionFalse"
                                        value={false}
                                    >
                                        {translateScreen('select_field_option.no')}
                                    </Option>
                                </SelectField>
                                <SelectField
                                    name="hasCamper"
                                    value={formData?.hasCamper}
                                    label={translateScreen('select_field_label.has_camper')}
                                    className={cx('global!ace-u-width--128')}
                                    onChange={value => onChange('hasCamper', value)}
                                >
                                    <Option
                                        name="hasCamperOptionNone"
                                        value=""
                                    >
                                        {translateScreen('select_field_option_placeholder.select_option')}
                                    </Option>
                                    <Option
                                        name="hasCamperOptionTrue"
                                        value={true}
                                    >
                                        {translateScreen('select_field_option.yes')}
                                    </Option>
                                    <Option
                                        name="hasCamperOptionFalse"
                                        value={false}
                                    >
                                        {translateScreen('select_field_option.no')}
                                    </Option>
                                </SelectField>
                                <SelectField
                                    name="carDismantling"
                                    value={formData?.carDismantling}
                                    label={translateScreen('select_field_label.car_disposal')}
                                    className={cx('global!ace-u-width--256')}
                                    onChange={value => onChange('carDismantling', value)}
                                    isMultipleChoice={true}
                                >
                                    {Object.keys(carDisposalOptionTypes).map(carDisposalOptionType => (
                                        <Option
                                            key={carDisposalOptionType}
                                            value={carDisposalOptionType}
                                            name={`carDisposalOption${camelCase(carDisposalOptionType)}`}
                                        >
                                            {translateScreen(`select_field_option.${snakeCase(carDisposalOptionType)}`)}
                                        </Option>
                                    ))}
                                </SelectField>
                            </div>
                            <div className={cx(['global!ace-u-typography--variant-body-medium', 'global!ace-u-margin--bottom-8'])}>
                                {translateScreen('text.assignment')}
                            </div>
                            <div
                                className={cx([
                                    'global!ace-u-flex',
                                    'global!ace-u-flex--column-gap-24',
                                    'global!ace-u-flex--align-flex-end',
                                    'global!ace-u-margin--bottom-32',
                                ])}
                            >
                                <SelectField
                                    name="fixedAreaOfApplication"
                                    value={formData?.fixedAreaOfApplication}
                                    label={translate('global.operation_area.fixed_area')}
                                    className={cx('global!ace-u-width--256')}
                                    onChange={value => onChange('fixedAreaOfApplication', value)}
                                >
                                    <Option
                                        name="fixedAreaOptionNone"
                                        value=""
                                    >
                                        {translateScreen('select_field_option_placeholder.select_option')}
                                    </Option>
                                    <Option
                                        name="fixedAreaOptionTrue"
                                        value={true}
                                    >
                                        {translateScreen('select_field_option.yes')}
                                    </Option>
                                    <Option
                                        name="fixedAreaOptionFalse"
                                        value={false}
                                    >
                                        {translateScreen('select_field_option.no')}
                                    </Option>
                                </SelectField>
                                <div>
                                    <Checkbox
                                        name="isFixedPriceEnabled"
                                        value={true}
                                        isSelected={formData?.isFixedPriceEnabled}
                                        onChange={value => onChange('isFixedPriceEnabled', value)}
                                    >
                                        <span
                                            className={cx([
                                                'global!ace-u-margin--top-4',
                                                'global!ace-u-typography--variant-caption',
                                            ])}
                                        >
                                            {translateScreen('range_field_label.fixed_price')}
                                        </span>
                                    </Checkbox>
                                    <RangeInput
                                        name="fixedPrice"
                                        value={formData?.fixedPrice}
                                        minValue={config.MIN_CONTRACT_PARTNER_FIXED_PRICE}
                                        maxValue={config.MAX_CONTRACT_PARTNER_FIXED_PRICE}
                                        step={10}
                                        className={cx('global!ace-u-width--256')}
                                        onChange={value => onChange('fixedPrice', value)}
                                        isDisabled={!formData?.isFixedPriceEnabled}
                                    />
                                </div>
                                <SelectField
                                    name="assignmentChannels"
                                    value={formData?.assignmentChannels}
                                    label={translateScreen('select_field_label.assignment_channel')}
                                    className={cx('global!ace-u-width--256')}
                                    onChange={value => onChange('assignmentChannels', value)}
                                    isMultipleChoice={true}
                                >
                                    {availableAssignmentChannels.map(channel => (
                                        <Option
                                            key={channel}
                                            value={channel}
                                            name={`assignmentChannelOption${camelCase(channel)}`}
                                        >
                                            {translate(
                                                `global.contract_partner_assignment_channel.${snakeCase(channel)}`,
                                            )}
                                        </Option>
                                    ))}
                                </SelectField>
                            </div>

                            <div className={cx(['global!ace-u-typography--variant-body-medium', 'global!ace-u-margin--bottom-8'])}>
                                {translateScreen('text.other')}
                            </div>
                            <div
                                className={cx([
                                    'global!ace-u-flex',
                                    'global!ace-u-flex--column-gap-24',
                                    'global!ace-u-margin--bottom-32',
                                ])}
                            >
                                <SelectField
                                    name="relations"
                                    value={formData?.relation}
                                    label={translateScreen('select_field_label.relations')}
                                    className={cx('global!ace-u-width--256')}
                                    onChange={value => onChange('relation', value)}
                                    isMultipleChoice={true}
                                >
                                    {Object.keys(relationOptionTypes).map(relation => (
                                        <Option
                                            key={relation}
                                            name={`relationOption${relation}`}
                                            value={relation}
                                        >
                                            {translateScreen(`select_field_option.${snakeCase(relation)}`)}
                                        </Option>
                                    ))}
                                </SelectField>
                                <SelectField
                                    name="externalCommissioners"
                                    value={formData?.externalCommissioners}
                                    label={translateScreen('select_field_label.external_commissioners')}
                                    className={cx('global!ace-u-width--256')}
                                    onChange={value => onChange('externalCommissioners', value)}
                                    isMultipleChoice={true}
                                >
                                    {Object.keys(apmContractPartnerExternalCommissionerTypes)
                                        .map(commissioner => (
                                            <Option
                                                key={`externalCommissioner${commissioner}`}
                                                value={commissioner}
                                                name={`externalCommissioner${commissioner}`}
                                            >
                                                {translate(
                                                    `global.contract_partner_external_commissioners.${snakeCase(
                                                        commissioner,
                                                    )}`,
                                                )}
                                            </Option>
                                        ))}
                                </SelectField>
                                <SelectField
                                    name="additionalFeatures"
                                    value={formData?.additionalFeatures}
                                    label={translateScreen('select_field_label.additional_features')}
                                    className={cx('global!ace-u-width--256')}
                                    onChange={value => onChange('additionalFeatures', value)}
                                    isMultipleChoice={true}
                                >
                                    {Object.keys(apmContractPartnerAdditionalFeatureTypes)
                                        .map(additionalFeature => (
                                            <Option
                                                key={additionalFeature}
                                                value={additionalFeature}
                                                name={`additionalFeatureOption${camelCase(additionalFeature)}`}
                                            >
                                                {translate(
                                                    `global.contract_partner_additional_features.${snakeCase(
                                                        additionalFeature,
                                                    )}`,
                                                )}
                                            </Option>
                                        ))}
                                </SelectField>
                                <SelectField
                                    name="ellaActive"
                                    value={formData?.ellaActive}
                                    label={translateScreen('select_field_label.ella_active')}
                                    className={cx('global!ace-u-width--128')}
                                    onChange={value => onChange('ellaActive', value)}
                                >
                                    <Option
                                        name="ellaActiveOptionNone"
                                        value=""
                                    >
                                        {translateScreen('select_field_option_placeholder.select_option')}
                                    </Option>
                                    <Option
                                        name="ellaActiveOptionTrue"
                                        value={true}
                                    >
                                        {translateScreen('select_field_option.yes')}
                                    </Option>
                                    <Option
                                        name="ellaActiveOptionFalse"
                                        value={false}
                                    >
                                        {translateScreen('select_field_option.no')}
                                    </Option>
                                </SelectField>
                            </div>
                        </Fragment>
                    )}
                    <div
                        className={cx([
                            'global!ace-u-flex',
                            'global!ace-u-flex--justify-space-between',
                        ])}
                    >
                        <div>
                            <span className={cx('global!ace-u-typography--variant-body')}>
                                {translateScreen('label.result_count')}
                            </span>&nbsp;
                            <span className={cx('global!ace-u-typography--variant-body-medium')}>
                                {contractPartnerSearchResultsCount}
                            </span>
                        </div>
                        <div
                            className={cx([
                                'global!ace-u-flex',
                                'global!ace-u-flex--align-center',
                            ])}
                        >
                            <div className={cx('global!ace-u-margin--right-64')}>
                                <InteractiveIcon
                                    icon={resetIcon}
                                    className={cx([
                                        'ace-c-interactive-icon--reverse',
                                        'ace-c-interactive-icon--highlight',
                                    ])}
                                    onClick={handleResetFilter}
                                >
                                    {translateScreen('interactive_icon_label.reset_filter')}
                                </InteractiveIcon>
                            </div>
                            <div>
                                <InteractiveIcon
                                    icon={!areFiltersExpanded ? arrowDownIcon : arrowUpIcon}
                                    className={cx([
                                        'ace-c-interactive-icon--reverse',
                                        'ace-c-interactive-icon--primary',
                                        'ace-c-interactive-icon--highlight',
                                    ])}
                                    onClick={() => setAreFiltersExpanded(prevState => !prevState)}
                                >
                                    {translateScreen('interactive_icon_label.advanced_search')}
                                </InteractiveIcon>
                            </div>
                        </div>
                    </div>
                </Fragment>
            </Panel>
            <Panel qaIdent="contract-partner-search-results" className={cx('ace-c-panel--full-bleed')}>
                <div
                    className={cx([
                        'global!ace-u-flex',
                        'global!ace-u-flex--justify-space-between',
                        'global!ace-u-flex--align-center',
                    ])}
                >
                    <h3
                        className={cx([
                            'global!ace-u-margin--32',
                            'global!ace-u-typography--variant-h3',
                        ])}
                    >
                        {translateScreen('panel_title.contract_partners')}
                    </h3>
                </div>
                <Table qaIdent="contract-partner-search-results">
                    {contractPartnerSearchResults.length === 0 && !isSearchInProgress && (
                        <TableCaption>
                            <NoResultsBlock
                                icon={(
                                    <Icon
                                        className={cx('ace-c-icon--xxl')}
                                        icon={findCaseIcon}
                                    />
                                )}
                                message={translateScreen('no_results.message')}
                                description={translateScreen('no_results.description')}
                            />
                        </TableCaption>
                    )}
                    {isSearchInProgress && (
                        <TableCaption>
                            <HighlightCircle
                                className={cx([
                                    'ace-c-highlight-circle--medium',
                                    'ace-c-highlight-circle--primary-highlight',
                                    'global!ace-u-margin--top-64',
                                    'global!ace-u-margin--bottom-64',
                                ])}
                            >
                                <LoaderSpinner />
                            </HighlightCircle>
                        </TableCaption>
                    )}
                    <TableHead>
                        <TableRow qaIdentPart="header">
                            <TableCell qaIdentPart="cp-name">
                                {translateScreen('table_header.name')}
                            </TableCell>
                            <TableCell qaIdentPart="cp-contact-details-phone">
                                {translateScreen('table_header.phone_number')}
                            </TableCell>
                            <TableCell qaIdentPart="cp-contract-status">
                                {translateScreen('table_header.status')}
                            </TableCell>
                            <TableCell qaIdentPart="cp-address-street">
                                {translateScreen('table_header.street')}
                            </TableCell>
                            <TableCell qaIdentPart="cp-address-post-code">
                                {translateScreen('table_header.post_code')}
                            </TableCell>
                            <TableCell qaIdentPart="cp-address-city">
                                {translateScreen('table_header.city')}
                            </TableCell>
                            <TableCell colSpan={2} qaIdentPart="cp-restrictions">
                                {translateScreen('table_header.restrictions')}
                            </TableCell>
                            <TableCell qaIdentPart="cp-restrictions-report" />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {contractPartnerSearchResults.length > 0 && !isSearchInProgress
                        && contractPartnerSearchResults.map(contractPartner => {
                            return (
                                <TableRow
                                    key={contractPartner.id}
                                    onClick={() => openContractPartnerScreen(contractPartner)}
                                    qaIdentPart={contractPartner.id}
                                >
                                    <TableCell
                                        qaIdentPart="cp-name"
                                        qaIdentPartPostfix={contractPartner.id}
                                    >
                                        {contractPartner.name || ''}
                                    </TableCell>
                                    <TableCell
                                        qaIdentPart="cp-contact-details-phone"
                                        qaIdentPartPostfix={contractPartner.id}
                                    >
                                        {contractPartner.businessContactDetails?.phoneNo || ''}
                                    </TableCell>
                                    <TableCell
                                        qaIdentPart="cp-contract-status"
                                        qaIdentPartPostfix={contractPartner.id}
                                    >
                                        {contractPartner.contractStatus
                                            ? translate(`global.contract_status.${snakeCase(contractPartner.contractStatus)}`)
                                            : ''
                                        }
                                    </TableCell>
                                    <TableCell
                                        qaIdentPart="cp-address-street"
                                        qaIdentPartPostfix={contractPartner.id}
                                    >
                                        {contractPartner.address?.street || ''}
                                    </TableCell>
                                    <TableCell
                                        qaIdentPart="cp-address-post-code"
                                        qaIdentPartPostfix={contractPartner.id}
                                    >
                                        {contractPartner.address?.postCode || ''}
                                    </TableCell>
                                    <TableCell
                                        qaIdentPart="cp-address-city"
                                        qaIdentPartPostfix={contractPartner.id}
                                    >
                                        {contractPartner.address?.city || ''}
                                    </TableCell>
                                    <TableCell
                                        colSpan={2}
                                        qaIdentPart="cp-restrictions"
                                        qaIdentPartPostfix={contractPartner.id}
                                    >
                                        {contractPartner.lastRestrictionType ? (
                                            <div
                                                className={cx([
                                                    'global!ace-u-inline-flex',
                                                    'global!ace-u-flex--align-center',
                                                    'global!ace-u-flex--justify-flex-start',
                                                ])}
                                            >
                                                <Icon
                                                    icon={categoryIconMap[contractPartner?.lastRestrictionType]}
                                                    className={cx(['ace-c-icon--m', 'global!ace-u-margin--right-8'])}
                                                />
                                                <div>
                                                    {translate(
                                                        `global.temporary_restriction_category.${snakeCase(
                                                            contractPartner?.lastRestrictionType,
                                                        )}`,
                                                    )}
                                                </div>
                                                {typeof contractPartner.activeRestrictions === 'number'
                                                && contractPartner.activeRestrictions > 1 && (
                                                    <Badge
                                                        className={cx([
                                                            'ace-c-badge--status-notification',
                                                            'global!ace-u-margin--left-8',
                                                        ])}
                                                    >
                                                        {`+${contractPartner.activeRestrictions - 1}`}
                                                    </Badge>
                                                )}
                                            </div>
                                        ) : ''}
                                    </TableCell>
                                    <TableCell
                                        qaIdentPart="cp-restrictions-report"
                                        qaIdentPartPostfix={contractPartner.id}
                                    >
                                        {!config.IS_FALLBACK_SYSTEM && (
                                            <InteractiveIcon
                                                className={cx([
                                                    'ace-c-interactive-icon--primary',
                                                    'global!ace-u-flex',
                                                ])}
                                                onClick={event => {
                                                    if (!isTemporaryRestrictionAvailable) return;
                                                    handleCreateRestriction(event, contractPartner);
                                                }}
                                                icon={plusIcon}
                                                isDisabled={!isTemporaryRestrictionAvailable}
                                            >
                                                {translateScreen('search_results_table_header_restrictions.create_new_report')}
                                            </InteractiveIcon>
                                        )}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </Panel>
            {contractPartnerSearchResults.length > 0 && !isSearchInProgress && (
                <Paginator
                    page={queryParams?.get('page') ? parseInt(queryParams.get('page'), 10) : 0}
                    count={paginatorCount}
                    onClick={handlePaginationPage}
                />
            )}
        </Fragment>
    );
};

ContractPartnerSearchScreen.propTypes = {
    history: PropTypes.object.isRequired,
    contractPartnerSearchResults: PropTypes.array,
    contractPartnerSearchResultsCount: PropTypes.number,
    initiateCreateTemporaryRestrictionFlow: PropTypes.func.isRequired,
    searchPersistenceState: PropTypes.string.isRequired,
};

ContractPartnerSearchScreen.defaultProps = {
    contractPartnerSearchResults: [],
    contractPartnerSearchResultsCount: 0,
};

const mapStateToProps = state => {
    return {
        contractPartnerSearchResults: contractPartnerSelectors.getContractPartnerSearchResults(state),
        contractPartnerSearchResultsCount: contractPartnerSelectors.getContractPartnerSearchResultsCount(state),
        searchPersistenceState: state.contractPartners.searchPersistenceState,
    };
};

const mapDispatchToProps = dispatch => ({
    initiateCreateTemporaryRestrictionFlow: payload => dispatch({
        type: contractPartnerActionTypes.INITIATE_CREATE_TEMPORARY_RESTRICTION_FLOW,
        payload,
    }),
});

export default connect(mapStateToProps, mapDispatchToProps)(ContractPartnerSearchScreen);
